/**
 * /admin/calls-and-links — the recurring live calls, the Book-a-call and
 * walkthrough links and the coach minimum on the organizations directory. Each is one admin setting (`ADMIN_SETTINGS`), saved on its own; until an
 * admin saves one, its default from code applies.
 *
 * Aliases: calls and links admin, office hours admin, live call schedule
 * admin, book a call link admin, walkthrough link admin, organization directory minimum admin
 */

import { useStore } from "@tanstack/react-form";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useAppForm } from "~/components/form/app-form";
import {
  FieldErrorsProvider,
  zodErrorLookup,
} from "~/components/form/fields/field-errors";
import { Button } from "~/components/ui/button";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminSettingsKeys,
  adminSettingsQueryOptions,
  saveAdminSettingFn,
  type SavedAdminSetting,
} from "~/lib/functions/admin-settings";
import { callsAndLinksKeys } from "~/lib/queries/callsAndLinksQuery";
import {
  ADMIN_SETTINGS,
  directoryMinCoachesSchema,
  liveCallSchema,
  type LiveCall,
} from "~/lib/shared/admin-settings";
import { describeWeeklyCall, WEEKDAY_NAMES } from "~/lib/shared/call-schedules";

import {
  liveCallFieldErrors,
  toFormValues,
  toLiveCallInput,
} from "./-components/live-call-form-values";

export const Route = createFileRoute("/admin/calls-and-links/")({
  staticData: { title: "Calls and links" },
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "calls-and-links:read"),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(adminSettingsQueryOptions());
  },
  component: CallsAndLinksPage,
});

function CallsAndLinksPage() {
  const { data: settings } = useSuspenseQuery(adminSettingsQueryOptions());

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-medium text-gray-900">Calls and links</h1>
        <p className="text-text-secondary max-w-2xl text-sm text-pretty">
          Set the weekly live calls coaches see, where &ldquo;Book a call&rdquo;
          and &ldquo;Book a walkthrough&rdquo; send them, and how many coaches
          an organization needs before its directory card shows the count. A
          change shows within a few minutes.
        </p>
      </div>
      <LiveCallForm saved={settings.office_hours} settingKey="office_hours" />
      <LiveCallForm
        saved={settings.accelerator_call}
        settingKey="accelerator_call"
      />
      <BookingLinkForm
        saved={settings.book_a_call_url}
        settingKey="book_a_call_url"
      />
      <BookingLinkForm
        saved={settings.marketing_suite_walkthrough_url}
        settingKey="marketing_suite_walkthrough_url"
      />
      <DirectoryMinCoachesForm saved={settings.directory_min_coaches} />
    </div>
  );
}

function useSaveSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveAdminSettingFn,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminSettingsKeys.admin }),
        queryClient.invalidateQueries({ queryKey: callsAndLinksKeys.coach }),
      ]);
      toast.success("Saved. The change shows within a few minutes.");
    },
    onError: () =>
      toast.error("Couldn't save. Check the fields and try again."),
  });
}

function SavedNote({ saved }: { saved: SavedAdminSetting<unknown> }) {
  if (!saved.savedAt) {
    return (
      <p className="text-text-tertiary text-xs">
        Using the default. No admin has saved it yet.
      </p>
    );
  }
  return (
    <p className="text-text-tertiary text-xs">
      Last saved{" "}
      {/* The server and the browser can sit on different sides of midnight. */}
      <time dateTime={saved.savedAt.toISOString()} suppressHydrationWarning>
        {saved.savedAt.toLocaleDateString("en-US", { dateStyle: "medium" })}
      </time>
      {saved.savedBy && ` by ${saved.savedBy}`}.
    </p>
  );
}

function SettingSection({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
      <div className="space-y-1">
        <h2 className="font-medium text-gray-900">{label}</h2>
        <p className="text-text-secondary max-w-2xl text-sm text-pretty">
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

// ── Weekly calls ────────────────────────────────────────────────────────────

const WEEKDAY_OPTIONS = WEEKDAY_NAMES.map((name, day) => ({
  value: String(day),
  label: name,
}));

function LiveCallForm({
  settingKey,
  saved,
}: {
  settingKey: "office_hours" | "accelerator_call";
  saved: SavedAdminSetting<LiveCall>;
}) {
  const setting = ADMIN_SETTINGS[settingKey];
  const save = useSaveSetting();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const form = useAppForm({ defaultValues: toFormValues(saved.value) });
  const values = useStore(form.store, (state) => state.values);
  const parsed = liveCallSchema.safeParse(toLiveCallInput(values));
  const errorFor = liveCallFieldErrors(
    parsed.success ? [] : parsed.error.issues,
    submitAttempted,
  );

  return (
    <SettingSection description={setting.description} label={setting.label}>
      <FieldErrorsProvider value={errorFor}>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (parsed.success) {
              save.mutate({ data: { key: settingKey, value: parsed.data } });
            }
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <form.AppField name="weekday">
              {(field) => (
                <field.SelectField
                  label="Day"
                  options={WEEKDAY_OPTIONS}
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="time">
              {(field) => <field.TextField label="Time" required type="time" />}
            </form.AppField>
            <form.AppField name="timeZone">
              {(field) => (
                <field.TextField
                  label="Time zone"
                  placeholder="America/New_York"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="zoneLabel">
              {(field) => (
                <field.TextField
                  label="Zone shown to coaches"
                  placeholder="ET"
                  required
                />
              )}
            </form.AppField>
          </div>
          <div className="space-y-1">
            <form.AppField name="joinUrl">
              {(field) => (
                <field.TextField
                  label="Join link"
                  placeholder="https://"
                  type="url"
                />
              )}
            </form.AppField>
            <p className="text-text-tertiary text-xs text-pretty">
              Leave it empty when the link changes every week. Coaches then see
              the schedule alone, and GoHighLevel sends the link in its reminder
              email.
            </p>
          </div>
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
            <span className="text-text-tertiary">Coaches read: </span>
            <span className="text-gray-900">
              {parsed.success
                ? describeWeeklyCall(parsed.data)
                : "Finish the fields above to see it."}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button loading={save.isPending} type="submit" variant="primary">
              Save
            </Button>
            <SavedNote saved={saved} />
          </div>
        </form>
      </FieldErrorsProvider>
    </SettingSection>
  );
}

// ── Booking links ────────────────────────────────────────────────────────────

const bookingLinkSchema = ADMIN_SETTINGS.book_a_call_url.schema;

/** One booking link: an https link, or empty for no link. */
function BookingLinkForm({
  settingKey,
  saved,
}: {
  settingKey: "book_a_call_url" | "marketing_suite_walkthrough_url";
  saved: SavedAdminSetting<string | null>;
}) {
  const setting = ADMIN_SETTINGS[settingKey];
  const save = useSaveSetting();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const form = useAppForm({ defaultValues: { url: saved.value ?? "" } });
  const url = useStore(form.store, (state) => state.values.url);
  const parsed = bookingLinkSchema.safeParse(url.trim() === "" ? null : url);
  const lookup = zodErrorLookup(
    parsed.success
      ? []
      : parsed.error.issues.map((issue) => ({ ...issue, path: ["url"] })),
    submitAttempted,
  );

  return (
    <SettingSection description={setting.description} label={setting.label}>
      <FieldErrorsProvider value={lookup}>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (parsed.success) {
              save.mutate({
                data: { key: settingKey, value: parsed.data },
              });
            }
          }}
        >
          <div className="max-w-xl">
            <form.AppField name="url">
              {(field) => (
                <field.TextField
                  label="Booking link"
                  placeholder="https://"
                  type="url"
                />
              )}
            </form.AppField>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button loading={save.isPending} type="submit" variant="primary">
              Save link
            </Button>
            <SavedNote saved={saved} />
          </div>
        </form>
      </FieldErrorsProvider>
    </SettingSection>
  );
}

// ── Coach count on organization cards ───────────────────────────────────────

function DirectoryMinCoachesForm({
  saved,
}: {
  saved: SavedAdminSetting<number>;
}) {
  const setting = ADMIN_SETTINGS.directory_min_coaches;
  const save = useSaveSetting();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const defaultValues: { minCoaches: number | null } = {
    minCoaches: saved.value,
  };
  const form = useAppForm({ defaultValues });
  const minCoaches = useStore(form.store, (state) => state.values.minCoaches);
  const parsed = directoryMinCoachesSchema.safeParse(minCoaches);
  const lookup = zodErrorLookup(
    parsed.success
      ? []
      : parsed.error.issues.map((issue) => ({
          ...issue,
          path: ["minCoaches"],
        })),
    submitAttempted,
  );

  return (
    <SettingSection description={setting.description} label={setting.label}>
      <FieldErrorsProvider value={lookup}>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (parsed.success) {
              save.mutate({
                data: { key: "directory_min_coaches", value: parsed.data },
              });
            }
          }}
        >
          <div className="max-w-40">
            <form.AppField name="minCoaches">
              {(field) => (
                <field.NumberField
                  label="Minimum coaches"
                  min={1}
                  required
                  step={1}
                />
              )}
            </form.AppField>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button loading={save.isPending} type="submit" variant="primary">
              Save
            </Button>
            <SavedNote saved={saved} />
          </div>
        </form>
      </FieldErrorsProvider>
    </SettingSection>
  );
}
